import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAutoRefresh } from '../hooks/useRealtimeState';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Video, User, Clock, Calendar,
  Wallet, Loader2, Check, AlertCircle, MessageSquare,
  FileText, Upload, Download, X, Pill, FlaskConical, Ban, MapPin, Phone, ShoppingCart, Tag, Copy
} from 'lucide-react';

import { openOnlineConsultationPrescriptionPrint } from '../components/PrescriptionTemplate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Force Jitsi meeting links to open in English, regardless of browser language
const joinMeetingUrl = (link) => {
  if (!link) return link;
  try {
    const u = new URL(link, window.location.origin);
    if (u.hostname.includes('jit.si')) u.searchParams.set('lang', 'en');
    return u.toString();
  } catch {
    return link;
  }
};

const copyMeetingLink = (link) => {
  navigator.clipboard.writeText(joinMeetingUrl(link));
  toast.success('Meeting link copied to clipboard');
};

// Computes whether a confirmed consultation is live now, starting soon (<=10min), or already ended.
// Instant/on-demand consultations (no scheduled date/time) are treated as Live immediately once confirmed.
const getConsultationLiveState = (c) => {
  if (c.status !== 'confirmed' || !c.meeting_link) return { isLive: false, isUpcoming: false, isEnded: false };
  const scheduledStr = c.scheduled_date || c.preferred_date;
  const timeStr = c.scheduled_time || c.preferred_time;
  if (!scheduledStr || !timeStr) {
    return { isLive: true, isUpcoming: false, isEnded: false, diffMin: 0 };
  }
  const scheduled = new Date(`${scheduledStr}T${timeStr}`);
  if (isNaN(scheduled.getTime())) return { isLive: true, isUpcoming: false, isEnded: false, diffMin: 0 };
  const diffMin = (scheduled.getTime() - Date.now()) / 60000;
  if (diffMin <= 10 && diffMin >= -60) return { isLive: true, isUpcoming: false, isEnded: false, diffMin };
  if (diffMin > 10) return { isLive: false, isUpcoming: true, isEnded: false, diffMin };
  return { isLive: false, isUpcoming: false, isEnded: true, diffMin };
};

export default function PatientConsultationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [doctors, setDoctors] = useState([]);
  const [myConsultations, setMyConsultations] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showBookDialog, setShowBookDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Clinic linking state
  const [linkedClinics, setLinkedClinics] = useState([]);
  const [clinicSearch, setClinicSearch] = useState('');
  const [clinicResults, setClinicResults] = useState([]);
  const [searchingClinics, setSearchingClinics] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'doctors';

  const handleTabChange = (val) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val === 'doctors') next.delete('tab');
      else next.set('tab', val);
      return next;
    }, { replace: true });
  };

  const getToken = () => localStorage.getItem('patient_token');

  const fetchData = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate('/patient/login'); return; }

    try {
      const [doctorsRes, consultRes, walletRes] = await Promise.all([
        fetch(`${API_URL}/api/patient-consultation/available-doctors${searchQuery ? `?search=${searchQuery}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-consultation/my-consultations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetch(`${API_URL}/api/patient-wallet/balance`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null)
      ]);

      if (doctorsRes?.ok) { const d = await doctorsRes.json(); setDoctors(d.doctors || []); }
      if (consultRes?.ok) { const d = await consultRes.json(); setMyConsultations(d.consultations || []); }
      if (walletRes?.ok) setWallet(await walletRes.json());

      // Fetch linked clinics
      const linkedRes = await fetch(`${API_URL}/api/patient-consultation/my-linked-clinics`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
      if (linkedRes?.ok) { const d = await linkedRes.json(); setLinkedClinics(d.clinics || []); }
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load data');
    }
    setLoading(false);
  }, [navigate, searchQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, 20000);


  const searchClinics = async (q) => {
    if (!q || q.length < 2) { setClinicResults([]); return; }
    setSearchingClinics(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-consultation/available-clinics?search=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (res.ok) { const d = await res.json(); setClinicResults(d.clinics || []); }
    } catch { setClinicResults([]); }
    setSearchingClinics(false);
  };

  const handleLinkClinic = async (clinicId) => {
    try {
      const res = await fetch(`${API_URL}/api/patient-consultation/link-clinic`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ clinic_id: clinicId })
      });
      const d = await res.json();
      if (res.ok) { toast.success(d.message || 'Linked!'); fetchData(); setClinicSearch(''); setClinicResults([]); }
      else toast.error(d.detail || 'Failed to link');
    } catch { toast.error('Network error'); }
  };

  const handleUnlinkClinic = async (clinicId) => {
    try {
      const res = await fetch(`${API_URL}/api/patient-consultation/unlink-clinic/${clinicId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) { toast.success('Unlinked'); fetchData(); }
      else toast.error('Failed to unlink');
    } catch { toast.error('Network error'); }
  };

  const handleRequestConsultation = async () => {
    if (!reason || !preferredDate) { toast.error('Please fill in required fields'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-consultation/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          doctor_id: selectedDoctor.id, reason,
          preferred_date: preferredDate,
          preferred_time: preferredTime || null, notes,
          coupon_code: couponApplied ? couponCode : null
        })
      });
      const data = await res.json();
      if (res.ok) {
        const msg = couponApplied ? `Request sent! Coupon ${couponApplied.code} applied — you pay ₹${couponApplied.final_fee}` : 'Consultation request sent!';
        toast.success(msg);
        setShowBookDialog(false);
        setSelectedDoctor(null);
        setReason(''); setPreferredDate(''); setPreferredTime(''); setNotes('');
        setCouponCode(''); setCouponApplied(null);
        fetchData();
      } else {
        toast.error(data.detail || 'Failed to request consultation');
      }
    } catch { toast.error('Network error'); }
    setSubmitting(false);
  };

  const handleConfirmPayment = async (consultationId) => {
    const consultation = myConsultations.find(c => c.id === consultationId);
    if (!consultation) return;
    const finalFee = consultation.final_fee ?? consultation.fee;
    if (wallet.balance < finalFee) { toast.error('Insufficient wallet balance'); return; }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-consultation/confirm/${consultationId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.discount > 0 ? `Paid ₹${data.fee_paid} (saved ₹${data.discount})!` : 'Consultation confirmed and paid!');
        fetchData();
      }
      else toast.error(data.detail || 'Failed to confirm');
    } catch { toast.error('Network error'); }
    setSubmitting(false);
  };

  const validateCoupon = async (fee) => {
    if (!couponCode.trim()) { toast.error('Enter a coupon code'); return; }
    setValidatingCoupon(true);
    try {
      const res = await fetch(`${API_URL}/api/consultation-coupons/validate?code=${encodeURIComponent(couponCode)}&fee=${fee}&doctor_id=${selectedDoctor?.id || ''}`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        setCouponApplied(data);
        toast.success(`Coupon applied! You save ₹${data.discount_amount}`);
      } else {
        toast.error(data.detail || 'Invalid coupon');
        setCouponApplied(null);
      }
    } catch { toast.error('Failed to validate coupon'); }
    setValidatingCoupon(false);
  };

  const handleCancelConsultation = async () => {
    const consultationId = selectedConsultation?.id;
    if (!consultationId) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/patient-consultation/cancel/${consultationId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ reason: cancelReason || 'Cancelled by patient' })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Consultation cancelled');
        if (data.refund_amount > 0) toast.success(`Refund of ₹${data.refund_amount} credited to wallet`);
        setShowCancelDialog(false);
        setCancelReason('');
        setSelectedConsultation(null);
        fetchData();
      } else toast.error(data.detail || 'Failed to cancel');
    } catch { toast.error('Network error'); }
    setSubmitting(false);
  };

  const handleUploadDocument = async (consultationId) => {
    fileInputRef.current?.click();
    fileInputRef.current.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await fetch(`${API_URL}/api/patient-consultation/upload-document/${consultationId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
          body: formData
        });
        if (res.ok) { toast.success('Document uploaded'); fetchData(); }
        else { const d = await res.json(); toast.error(d.detail || 'Upload failed'); }
      } catch { toast.error('Upload failed'); }
      setUploading(false);
      fileInputRef.current.value = '';
    };
  };

  const getStatusBadge = (status) => {
    const map = {
      requested: <Badge variant="outline" data-testid="status-pending">Pending Approval</Badge>,
      approved: <Badge variant="secondary" data-testid="status-approved">Awaiting Payment</Badge>,
      confirmed: <Badge className="bg-green-600" data-testid="status-confirmed">Confirmed</Badge>,
      completed: <Badge className="bg-blue-600" data-testid="status-completed">Completed</Badge>,
      cancelled: <Badge variant="destructive" data-testid="status-cancelled">Cancelled</Badge>,
      rejected: <Badge className="bg-red-100 text-red-700" data-testid="status-rejected">Rejected</Badge>
    };
    return map[status] || <Badge variant="outline">{status}</Badge>;
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
    </div>
  );

  const liveConsultation = myConsultations.find(c => getConsultationLiveState(c).isLive);

  return (
    <div className="min-h-screen bg-slate-50">
      <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" />

      {/* Dynamic Telehealth Live Lock-Bar - sticky, pulse-animated when a video consult is live or starting within 10 min */}
      {liveConsultation && (
        <button
          onClick={() => window.open(joinMeetingUrl(liveConsultation.meeting_link), '_blank')}
          className="w-full sticky top-0 z-[60] bg-emerald-600 text-white px-4 py-3 flex items-center justify-center gap-2 font-bold text-sm sm:text-base shadow-md border-b border-emerald-700 animate-pulse hover:bg-emerald-700 transition-colors"
          data-testid="live-consultation-lock-bar"
        >
          <Video className="h-5 w-5" />
          Your Video Consultation is Live: <span className="underline">Tap to Join Consultation Room</span>
        </button>
      )}

      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/patient/dashboard')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-bold text-lg">Online Consultations</h1>
              <p className="text-sm text-muted-foreground">Book video consultations with doctors</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => window.location.href = 'https://immuski.com'} data-testid="back-to-immuski-btn" className="text-violet-600 border-violet-200 hover:bg-violet-50">
              <ArrowLeft className="h-4 w-4 mr-1" /> Immuski
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/patient/wallet')} data-testid="wallet-btn">
              <Wallet className="h-4 w-4 mr-2" />₹{wallet.balance?.toFixed(0) || 0}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue={defaultTab} onValueChange={handleTabChange}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="doctors" className="flex-1" data-testid="tab-find-doctors">Find Doctors</TabsTrigger>
            <TabsTrigger value="my-consultations" className="flex-1" data-testid="tab-my-consultations">My Consultations</TabsTrigger>
            <TabsTrigger value="linked-clinics" className="flex-1" data-testid="tab-linked-clinics">My Clinics</TabsTrigger>
          </TabsList>

          {/* Find Doctors Tab */}
          <TabsContent value="doctors">
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mb-4 flex items-center gap-3" data-testid="coupon-hint-banner">
              <Tag className="h-5 w-5 text-violet-600 shrink-0" />
              <p className="text-sm text-violet-700">
                <strong>Have a coupon?</strong> Apply it while booking to get discounted or free consultations!
              </p>
            </div>
            <div className="mb-6 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search doctors by name or specialty..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="doctor-search" />
            </div>

            {doctors.length === 0 ? (
              <div className="text-center py-12">
                <Video className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No doctors available for online consultation</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {doctors.map((doctor) => (
                  <Card key={doctor.id} className="hover:shadow-md transition-shadow" data-testid={`doctor-card-${doctor.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-3 bg-teal-100 rounded-full">
                          <User className="h-6 w-6 text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold">{doctor.name}</p>
                          <p className="text-sm text-muted-foreground">{doctor.specialty}</p>
                          <p className="text-xs text-muted-foreground">{doctor.clinic_name}</p>
                          {doctor.clinic_address && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="h-3 w-3 shrink-0" /> {doctor.clinic_address}
                            </p>
                          )}
                          {doctor.clinic_phone && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3 shrink-0" /> {doctor.clinic_phone}
                            </p>
                          )}
                          {doctor.online_slots && doctor.online_slots.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-slate-500">Online Availability</p>
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {doctor.online_slots.filter(s => s.is_available).map(s => {
                                  const times = [s.start_time && s.end_time ? `${s.start_time}-${s.end_time}` : null, ...(s.extra_slots || []).map(es => es.start && es.end ? `${es.start}-${es.end}` : null)].filter(Boolean);
                                  return (
                                    <span key={s.day} className="text-[10px] bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded capitalize">
                                      {s.day.slice(0, 3)} {times.join(', ')}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div className="mt-3 flex items-center justify-between">
                            <div>
                              <p className="text-lg font-bold text-teal-600">₹{doctor.consultation_fee}</p>
                              <p className="text-xs text-muted-foreground">per consultation</p>
                            </div>
                            <Button size="sm" onClick={() => { setSelectedDoctor(doctor); setShowBookDialog(true); }}
                              data-testid={`book-btn-${doctor.id}`}>
                              <Video className="h-4 w-4 mr-1" /> Book
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Consultations Tab */}
          <TabsContent value="my-consultations">
            {myConsultations.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No consultations yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myConsultations.map((c) => (
                  <Card key={c.id} data-testid={`my-consultation-${c.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-teal-100 rounded-full">
                            <User className="h-5 w-5 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{c.doctor_name}</p>
                            <p className="text-sm text-muted-foreground">{c.doctor_specialty}</p>
                            <p className="text-sm mt-1">{c.reason}</p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {c.scheduled_date || c.preferred_date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {c.scheduled_time || c.preferred_time || 'TBD'}
                              </span>
                            </div>

                            {/* Rejection reason */}
                            {c.status === 'rejected' && c.rejection_reason && (
                              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="rejection-reason">
                                <strong>Reason for rejection:</strong> {c.rejection_reason}
                              </div>
                            )}

                            {/* Cancellation reason */}
                            {c.status === 'cancelled' && c.cancellation_reason && (
                              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-700" data-testid="cancellation-reason">
                                <strong>Cancelled by {c.cancelled_by}:</strong> {c.cancellation_reason}
                                {c.refund_amount > 0 && <span className="ml-1">(₹{c.refund_amount} refunded)</span>}
                              </div>
                            )}

                            {/* Documents */}
                            {c.documents && c.documents.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs text-slate-400 mb-1">Documents ({c.documents.length})</p>
                                <div className="flex flex-wrap gap-2">
                                  {c.documents.map(doc => (
                                    <a key={doc.id} href={`${API_URL}/api/patient-consultation/document/${doc.file_name}`}
                                      target="_blank" rel="noreferrer"
                                      className="text-xs text-blue-600 hover:underline flex items-center gap-1 bg-blue-50 px-2 py-1 rounded">
                                      <FileText className="h-3 w-3" /> {doc.original_name}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="font-bold">₹{c.fee}</p>
                          {getStatusBadge(c.status)}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                        {c.status === 'approved' && (
                          <div className="w-full space-y-2">
                            {c.coupon_code && (
                              <div className="bg-green-50 border border-green-200 rounded p-2 text-sm text-green-700">
                                <Check className="h-3 w-3 inline mr-1" />
                                Coupon <strong>{c.coupon_code}</strong> applied — Discount: ₹{c.coupon_discount} — You pay: <strong>₹{c.final_fee}</strong>
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Button onClick={() => handleConfirmPayment(c.id)}
                                disabled={submitting || wallet.balance < (c.final_fee ?? c.fee)}
                                data-testid={`confirm-pay-${c.id}`}>
                                {wallet.balance >= (c.final_fee ?? c.fee) ? (
                                  <><Check className="h-4 w-4 mr-1" /> Confirm & Pay ₹{c.final_fee ?? c.fee}</>
                                ) : (
                                  <><AlertCircle className="h-4 w-4 mr-1" /> Insufficient Balance</>
                                )}
                              </Button>
                              <Button variant="outline"
                                onClick={() => { setSelectedConsultation(c); setShowCancelDialog(true); }}
                                disabled={submitting}>Cancel</Button>
                            </div>
                          </div>
                        )}

                        {c.status === 'confirmed' && (() => {
                          const liveState = getConsultationLiveState(c);
                          return (
                          <>
                            {c.meeting_link && (
                              <>
                                {liveState.isLive ? (
                                  <Button onClick={() => window.open(joinMeetingUrl(c.meeting_link), '_blank')} data-testid={`join-call-${c.id}`} className="bg-emerald-600 hover:bg-emerald-700">
                                    <Video className="h-4 w-4 mr-1" /> Join Call
                                  </Button>
                                ) : (
                                  <Button disabled className="bg-slate-200 text-slate-400 cursor-not-allowed" data-testid={`join-call-${c.id}`}>
                                    <Video className="h-4 w-4 mr-1" />
                                    {liveState.isUpcoming ? `Starts in ${Math.ceil(liveState.diffMin)}m` : 'Consultation Ended'}
                                  </Button>
                                )}
                                <Button variant="outline" size="icon" onClick={() => copyMeetingLink(c.meeting_link)}
                                  disabled={!liveState.isLive}
                                  data-testid={`copy-link-${c.id}`} title="Copy meeting link">
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button variant="outline" size="sm" onClick={() => handleUploadDocument(c.id)}
                              disabled={uploading} data-testid={`upload-doc-${c.id}`}>
                              <Upload className="h-4 w-4 mr-1" /> {uploading ? 'Uploading...' : 'Upload Document'}
                            </Button>
                            <Button variant="outline" className="text-red-500"
                              onClick={() => { setSelectedConsultation(c); setShowCancelDialog(true); }}
                              disabled={submitting}>
                              <Ban className="h-4 w-4 mr-1" /> Cancel
                            </Button>
                          </>
                          );
                        })()}

                        {c.status === 'requested' && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handleUploadDocument(c.id)}
                              disabled={uploading}>
                              <Upload className="h-4 w-4 mr-1" /> Upload Document
                            </Button>
                            <Button variant="outline"
                              onClick={() => { setSelectedConsultation(c); setShowCancelDialog(true); }}
                              disabled={submitting}>Cancel Request</Button>
                          </>
                        )}

                        {(c.status === 'completed' || c.status === 'confirmed') && c.prescription && (
                          <Button variant="outline" size="sm"
                            onClick={() => { setSelectedConsultation(c); setShowPrescriptionDialog(true); }}
                            data-testid={`view-rx-${c.id}`}>
                            <FileText className="h-4 w-4 mr-1" /> View Prescription
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Linked Clinics Tab */}
          <TabsContent value="linked-clinics">
            <div className="space-y-6">
              {/* Search and link new clinic */}
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-3">Link with a Clinic</h3>
                  <p className="text-sm text-slate-500 mb-3">Search by clinic name, doctor name, or phone number to link your account</p>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search clinics..."
                      value={clinicSearch}
                      onChange={(e) => { setClinicSearch(e.target.value); searchClinics(e.target.value); }}
                      className="pl-10"
                      data-testid="clinic-search-input"
                    />
                    {searchingClinics && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-slate-400" />}
                  </div>
                  {clinicResults.length > 0 && (
                    <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                      {clinicResults.map((c) => {
                        const alreadyLinked = linkedClinics.some(lc => lc.id === c.id);
                        return (
                          <div key={c.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 border-b last:border-b-0">
                            <div>
                              <p className="font-medium text-sm">{c.clinic_name || c.name}</p>
                              <p className="text-xs text-slate-500">{c.specialization} {c.phone && `| ${c.phone}`}</p>
                            </div>
                            {alreadyLinked ? (
                              <Badge className="bg-green-100 text-green-700 text-xs">Linked</Badge>
                            ) : (
                              <Button size="sm" onClick={() => handleLinkClinic(c.id)} data-testid={`link-clinic-${c.id}`}>Link</Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Linked clinics list */}
              <div>
                <h3 className="font-semibold mb-3">My Linked Clinics ({linkedClinics.length})</h3>
                {linkedClinics.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <MapPin className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No clinics linked yet. Search above to link with your clinic.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-3">
                    {linkedClinics.map((c) => (
                      <Card key={c.id}>
                        <CardContent className="py-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                              <User className="w-5 h-5 text-teal-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{c.clinic_name || c.name}</p>
                              <p className="text-xs text-slate-500">{c.specialization} {c.phone && `| ${c.phone}`}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleUnlinkClinic(c.id)} data-testid={`unlink-clinic-${c.id}`}>
                            <X className="w-4 h-4 mr-1" /> Unlink
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </div>

      {/* Book Consultation Dialog */}
      <Dialog open={showBookDialog} onOpenChange={setShowBookDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Consultation</DialogTitle>
          </DialogHeader>
          {selectedDoctor && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="p-2 bg-teal-100 rounded-full">
                  <User className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold">{selectedDoctor.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedDoctor.specialty}</p>
                </div>
                <p className="ml-auto font-bold text-teal-600">₹{selectedDoctor.consultation_fee}</p>
              </div>

              <div className="space-y-2">
                <Label>Reason for Consultation *</Label>
                <Input placeholder="Describe your concern..." value={reason}
                  onChange={(e) => setReason(e.target.value)} data-testid="consultation-reason" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Preferred Date *</Label>
                  <Input type="date" value={preferredDate}
                    onChange={(e) => { setPreferredDate(e.target.value); setPreferredTime(''); }}
                    min={new Date().toISOString().split('T')[0]} data-testid="consultation-date" />
                </div>
                <div className="space-y-2">
                  <Label>Preferred Time</Label>
                  {(() => {
                    if (!preferredDate || !selectedDoctor?.online_slots?.length) {
                      return <Input type="time" value={preferredTime}
                        onChange={(e) => setPreferredTime(e.target.value)} data-testid="consultation-time" />;
                    }
                    const dayName = new Date(preferredDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
                    const daySlot = selectedDoctor.online_slots.find(s => s.day === dayName);
                    if (!daySlot || !daySlot.is_available) {
                      return (
                        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600" data-testid="day-unavailable">
                          Doctor is not available on {dayName}s for online consultation
                        </div>
                      );
                    }
                    // Generate 30-min slots from all time ranges
                    const slots = [];
                    const addRange = (startStr, endStr) => {
                      if (!startStr || !endStr) return;
                      const [sh, sm] = startStr.split(':').map(Number);
                      const [eh, em] = endStr.split(':').map(Number);
                      let cur = sh * 60 + sm;
                      const end = eh * 60 + em;
                      while (cur < end) {
                        const h = String(Math.floor(cur / 60)).padStart(2, '0');
                        const m = String(cur % 60).padStart(2, '0');
                        slots.push(`${h}:${m}`);
                        cur += 30;
                      }
                    };
                    addRange(daySlot.start_time || '10:00', daySlot.end_time || '13:00');
                    if (daySlot.extra_slots) {
                      daySlot.extra_slots.forEach(es => addRange(es.start, es.end));
                    }
                    return (
                      <div className="flex flex-wrap gap-2 mt-1" data-testid="time-slots">
                        {slots.map(t => (
                          <Button key={t} size="sm" type="button"
                            variant={preferredTime === t ? 'default' : 'outline'}
                            className={preferredTime === t ? 'bg-teal-600 hover:bg-teal-700' : ''}
                            onClick={() => setPreferredTime(t)}>
                            {t}
                          </Button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea placeholder="Any additional information..." value={notes}
                  onChange={(e) => setNotes(e.target.value)} data-testid="consultation-notes" />
              </div>

              {/* Coupon Code */}
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
                <Label className="text-sm text-violet-700 font-medium">Have a coupon code?</Label>
                <div className="flex gap-2">
                  <Input placeholder="Enter coupon code" value={couponCode}
                    onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponApplied(null); }}
                    className="bg-white h-9" data-testid="coupon-input" />
                  <Button variant="outline" size="sm" className="h-9 shrink-0"
                    onClick={() => validateCoupon(selectedDoctor.consultation_fee)}
                    disabled={validatingCoupon || !couponCode.trim()} data-testid="apply-coupon-btn">
                    {validatingCoupon ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
                {couponApplied && (
                  <div className="bg-green-50 border border-green-200 rounded p-2 flex items-center justify-between text-sm">
                    <span className="text-green-700">
                      <Check className="h-3 w-3 inline mr-1" />
                      {couponApplied.discount_type === 'percentage' ? `${couponApplied.discount_value}% off` : `₹${couponApplied.discount_value} off`}
                      {' '}&mdash; You save ₹{couponApplied.discount_amount}!
                      {couponApplied.final_fee === 0 && <strong> FREE consultation!</strong>}
                    </span>
                    <button className="text-red-500" onClick={() => { setCouponApplied(null); setCouponCode(''); }}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>

              {/* Price Summary */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Consultation Fee</span>
                  <span className={couponApplied ? 'line-through text-slate-400' : 'font-medium'}>₹{selectedDoctor.consultation_fee}</span>
                </div>
                {couponApplied && (
                  <>
                    <div className="flex justify-between text-green-600">
                      <span>Coupon ({couponApplied.code})</span>
                      <span>-₹{couponApplied.discount_amount}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t pt-1">
                      <span>You Pay</span>
                      <span className="text-teal-700">₹{couponApplied.final_fee}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="bg-blue-50 text-blue-700 p-3 rounded-lg text-sm">
                <p className="font-medium">How it works:</p>
                <ol className="list-decimal ml-4 mt-1 space-y-1">
                  <li>Submit your request{couponApplied ? ' (coupon applied)' : ''}</li>
                  <li>Doctor reviews and approves</li>
                  <li>Pay {couponApplied ? `₹${couponApplied.final_fee}` : `₹${selectedDoctor.consultation_fee}`} from wallet</li>
                  <li>Receive meeting link for video call</li>
                </ol>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookDialog(false)}>Cancel</Button>
            <Button onClick={handleRequestConsultation} disabled={submitting || !reason || !preferredDate}
              data-testid="submit-consultation-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Consultation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedConsultation?.payment_status === 'paid' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                You will receive a full refund of ₹{selectedConsultation.fee} to your wallet.
              </div>
            )}
            <div>
              <Label>Reason for Cancellation</Label>
              <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Reason for cancellation (optional)..." className="mt-1" data-testid="patient-cancel-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCancelDialog(false); setCancelReason(''); }}>Back</Button>
            <Button variant="destructive" onClick={handleCancelConsultation} disabled={submitting} data-testid="confirm-patient-cancel">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Cancel Consultation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prescription View Dialog */}
      <Dialog open={showPrescriptionDialog} onOpenChange={setShowPrescriptionDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Prescription</DialogTitle>
          </DialogHeader>
          {selectedConsultation?.prescription && (
            <div className="space-y-4" data-testid="prescription-view">
              <div className="bg-teal-50 p-3 rounded-lg">
                <p className="text-sm text-slate-500">Doctor</p>
                <p className="font-medium">{selectedConsultation.prescription.doctor_name}</p>
                <p className="text-sm text-slate-500">{selectedConsultation.prescription.doctor_specialty}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-600">Diagnosis</p>
                <p className="font-semibold">{selectedConsultation.prescription.diagnosis}</p>
              </div>

              {selectedConsultation.prescription.medicines?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-1">
                    <Pill className="h-4 w-4" /> Medicines
                  </p>
                  <div className="space-y-2">
                    {selectedConsultation.prescription.medicines.map((m, i) => (
                      <div key={`el-${i}`} className="p-2 bg-slate-50 rounded-lg text-sm">
                        <p className="font-medium">{m.name}</p>
                        <p className="text-slate-500">{m.dosage} | {m.frequency} | {m.duration}</p>
                        {m.instructions && <p className="text-xs text-slate-400 mt-1">{m.instructions}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedConsultation.prescription.tests?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-1">
                    <FlaskConical className="h-4 w-4" /> Tests
                  </p>
                  <div className="space-y-1">
                    {selectedConsultation.prescription.tests.map((t, i) => (
                      <div key={`el-${i}`} className="p-2 bg-slate-50 rounded-lg text-sm">
                        <span className="font-medium">{t.name}</span>
                        {t.instructions && <span className="text-slate-400 ml-2">- {t.instructions}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedConsultation.prescription.advice && (
                <div>
                  <p className="text-sm font-medium text-slate-600">Advice</p>
                  <p className="text-sm">{selectedConsultation.prescription.advice}</p>
                </div>
              )}

              {selectedConsultation.prescription.follow_up_date && (
                <div>
                  <p className="text-sm font-medium text-slate-600">Follow-up Date</p>
                  <p className="text-sm">{selectedConsultation.prescription.follow_up_date}</p>
                </div>
              )}

              <p className="text-xs text-slate-400">
                Generated on {new Date(selectedConsultation.prescription.created_at).toLocaleString()}
              </p>

              {/* Prescription Barcode for Chemist Scan */}
              {(selectedConsultation.prescription_barcode || selectedConsultation.prescription?.prescription_barcode) && (
                <div className="bg-slate-50 border border-slate-200 p-3 rounded-lg text-center" data-testid="prescription-barcode-display">
                  <p className="text-xs text-slate-500 mb-2">Show this QR at any linked chemist</p>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(`${process.env.REACT_APP_BACKEND_URL}/scan/${selectedConsultation.prescription_barcode || selectedConsultation.prescription?.prescription_barcode}`)}`}
                    alt="Prescription QR"
                    className="mx-auto mb-1"
                    style={{ width: 100, height: 100 }}
                  />
                  <p className="text-sm font-mono font-bold tracking-widest text-slate-800">
                    {selectedConsultation.prescription_barcode || selectedConsultation.prescription?.prescription_barcode}
                  </p>
                </div>
              )}

              {/* Order Medicines Button */}
              {(selectedConsultation.prescription_barcode || selectedConsultation.prescription?.prescription_barcode) && selectedConsultation.prescription?.medicines?.length > 0 && (
                <Button
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => navigate(`/patient/marketplace?rx=${selectedConsultation.prescription_barcode || selectedConsultation.prescription?.prescription_barcode}`)}
                  data-testid="order-medicines-from-consultation"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" /> Order Medicines from this Prescription
                </Button>
              )}

              <Button className="w-full mt-2" variant="outline"
                onClick={() => openOnlineConsultationPrescriptionPrint(selectedConsultation)}
                data-testid="download-prescription-btn">
                <Download className="h-4 w-4 mr-2" /> Download / Print Prescription
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
